import React, { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { PlusIcon, PencilIcon } from '@heroicons/react/24/outline';
import { tratamientosApi } from '../../services/api';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import type { Tratamiento } from '../../types/api';

// Función helper para evaluar si un tratamiento está activo
const isTreatmentActive = (activo: any): boolean => {
  if (typeof activo === 'boolean') return activo;
  if (typeof activo === 'string') {
    return activo.toLowerCase() === 'activo' || activo.toLowerCase() === 'true' || activo === '1';
  }
  if (typeof activo === 'number') return activo === 1;
  return false;
};

// Componente Switch para el estado
function StatusSwitch({ 
  isActive, 
  onToggle, 
  loading = false 
}: { 
  isActive: boolean; 
  onToggle: () => void; 
  loading?: boolean; 
}) {
  return (
    <button
      onClick={onToggle}
      disabled={loading}
      className={`
        relative inline-flex h-6 w-11 items-center rounded-full transition-colors
        ${isActive ? 'bg-green-600' : 'bg-red-600'}
        ${loading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
      `}
    >
      <span
        className={`
          inline-block h-4 w-4 transform rounded-full bg-white transition-transform
          ${isActive ? 'translate-x-6' : 'translate-x-1'}
        `}
      />
    </button>
  );
}

export function TreatmentsPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [isAdding, setIsAdding] = useState(false);
  const [editingTreatmentId, setEditingTreatmentId] = useState<number | null>(null);
  const [editingTreatment, setEditingTreatment] = useState<any>(null);
  const [newTreatment, setNewTreatment] = useState({
    nombre: '',
    descripcion: '',
    duracion_min: '',
    precio_eur: '',
    activo: true
  });

  // Filtros y paginación
  const [inputFilters, setInputFilters] = useState({
    nombre: '',
    precio_min: '',
    precio_max: '',
    fecha_inicio: '',
    fecha_fin: ''
  });
  const [filters, setFilters] = useState(inputFilters);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);

  // Obtener lista de tratamientos con filtros y paginación
  const { data, isLoading } = useQuery<any, Error>({
    queryKey: ['treatments', filters, page, limit],
    queryFn: () => tratamientosApi.getAll({ ...filters, page, limit }),
    staleTime: 1000 * 60
  });
  const treatments: Tratamiento[] = data?.data || [];
  const total: number = data?.total || 0;

  // Mutación para crear tratamiento
  const createMutation = useMutation({
    mutationFn: tratamientosApi.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['treatments'] });
      toast.success('Tratamiento creado exitosamente');
      setIsAdding(false);
      setNewTreatment({
        nombre: '',
        descripcion: '',
        duracion_min: '',
        precio_eur: '',
        activo: true
      });
    },
    onError: () => {
      toast.error(t('common.error'));
    },
  });

  // Mutación para actualizar tratamiento
  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: number; data: any }) => {
      return tratamientosApi.update(id, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['treatments'] });
      toast.success('Tratamiento actualizado exitosamente');
      setEditingTreatmentId(null);
      setEditingTreatment(null);
    },
    onError: () => {
      toast.error(t('common.error'));
    },
  });

  // Mutación para actualizar estado del tratamiento
  const updateStatusMutation = useMutation({
    mutationFn: async ({ id, activo }: { id: number; activo: boolean }) => {
      return tratamientosApi.update(id, { activo });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['treatments'] });
      toast.success('Estado del tratamiento actualizado');
    },
    onError: () => {
      toast.error(t('common.error'));
    },
  });

  const handleToggleStatus = (treatment: Tratamiento) => {
    const currentStatus = isTreatmentActive(treatment.activo);
    updateStatusMutation.mutate({
      id: treatment.id,
      activo: !currentStatus
    });
  };

  const handleAddNew = () => {
    setIsAdding(true);
  };

  const handleEdit = (treatment: Tratamiento) => {
    setEditingTreatmentId(treatment.id);
    setEditingTreatment({
      nombre: treatment.nombre || '',
      descripcion: treatment.descripcion || '',
      duracion_min: treatment.duracion_min || treatment.duracion_minutos || '',
      precio_eur: treatment.precio_eur || treatment.precio || '',
      activo: treatment.activo || false
    });
  };

  const handleSaveNew = () => {
    if (!newTreatment.nombre) {
      toast.error('Por favor, completa el campo obligatorio: Nombre');
      return;
    }
    createMutation.mutate(newTreatment as any);
  };

  const handleSaveEdit = () => {
    if (!editingTreatment.nombre) {
      toast.error('Por favor, completa el campo obligatorio: Nombre');
      return;
    }
    updateMutation.mutate({
      id: editingTreatmentId!,
      data: editingTreatment
    });
  };

  const handleCancelNew = () => {
    setIsAdding(false);
    setNewTreatment({
      nombre: '',
      descripcion: '',
      duracion_min: '',
      precio_eur: '',
      activo: true
    });
  };

  const handleCancelEdit = () => {
    setEditingTreatmentId(null);
    setEditingTreatment(null);
  };

  const handleInputChange = (field: string, value: any) => {
    setNewTreatment(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleEditInputChange = (field: string, value: any) => {
    setEditingTreatment((prev: any) => ({
      ...prev,
      [field]: value
    }));
  };

  // Función para formatear precio
  const formatPrice = (price?: number | string) => {
    if (!price || price === '') return '-';
    const numPrice = typeof price === 'string' ? parseFloat(price) : price;
    if (isNaN(numPrice)) return '-';
    return `${numPrice.toFixed(2)} €`;
  };

  // Función para formatear duración
  const formatDuration = (duration?: number | string) => {
    if (!duration || duration === '') return '-';
    const numDuration = typeof duration === 'string' ? parseInt(duration) : duration;
    if (isNaN(numDuration)) return '-';
    return `${numDuration} min`;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" text="Cargando tratamientos..." />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">
            {t('treatments.title')}
          </h1>
          <p className="mt-2 text-gray-600 dark:text-gray-400">
            Gestiona la información de los tratamientos de la clínica
          </p>
        </div>
        <button
          onClick={handleAddNew}
          disabled={isAdding || editingTreatmentId !== null}
          className="btn-primary disabled:opacity-50"
        >
          <PlusIcon className="w-5 h-5 mr-2" />
          Agregar Tratamiento
        </button>
      </div>

      {/* Filtros */}
      <div className="flex flex-wrap gap-4 items-end bg-gray-50 dark:bg-gray-800 p-4 rounded-lg mb-2">
        <div>
          <label className="block text-xs font-medium text-gray-700 dark:text-gray-200">Nombre</label>
          <input type="text" value={inputFilters.nombre} onChange={e => setInputFilters(f => ({ ...f, nombre: e.target.value }))} className="input input-sm bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-500 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-400" />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-700 dark:text-gray-200">Precio mínimo</label>
          <input type="number" value={inputFilters.precio_min} onChange={e => setInputFilters(f => ({ ...f, precio_min: e.target.value }))} className="input input-sm bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-500 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-400" min="0" />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-700 dark:text-gray-200">Precio máximo</label>
          <input type="number" value={inputFilters.precio_max} onChange={e => setInputFilters(f => ({ ...f, precio_max: e.target.value }))} className="input input-sm bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-500 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-400" min="0" />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-700 dark:text-gray-200">Fecha alta desde</label>
          <input type="date" value={inputFilters.fecha_inicio} onChange={e => setInputFilters(f => ({ ...f, fecha_inicio: e.target.value }))} className="input input-sm bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-500 text-gray-900 dark:text-gray-100" />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-700 dark:text-gray-200">Fecha alta hasta</label>
          <input type="date" value={inputFilters.fecha_fin} onChange={e => setInputFilters(f => ({ ...f, fecha_fin: e.target.value }))} className="input input-sm bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-500 text-gray-900 dark:text-gray-100" />
        </div>
        <div>
          <button onClick={() => { setFilters(inputFilters); setPage(1); }} className="btn btn-sm bg-blue-600 hover:bg-blue-700 text-white">Buscar</button>
          <button onClick={() => { setInputFilters({ nombre: '', precio_min: '', precio_max: '', fecha_inicio: '', fecha_fin: '' }); setFilters({ nombre: '', precio_min: '', precio_max: '', fecha_inicio: '', fecha_fin: '' }); setPage(1); }} className="btn btn-sm ml-2 bg-gray-200 dark:bg-gray-700 dark:text-gray-200">Limpiar</button>
        </div>
      </div>

      {/* Tabla de tratamientos */}
      <div className="card">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-800">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  ID
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Nombre
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Descripción
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Duración (min)
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Precio (€)
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Activo
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
              {/* Fila para agregar nuevo tratamiento */}
              {isAdding && (
                <tr className="bg-blue-50 dark:bg-blue-900/20">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    Auto
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <input
                      type="text"
                      value={newTreatment.nombre}
                      onChange={(e) => handleInputChange('nombre', e.target.value)}
                      className="w-full px-2 py-1 text-sm border rounded dark:bg-gray-800 dark:border-gray-600"
                      placeholder="Nombre *"
                      required
                    />
                  </td>
                  <td className="px-6 py-4">
                    <textarea
                      value={newTreatment.descripcion}
                      onChange={(e) => handleInputChange('descripcion', e.target.value)}
                      className="w-full px-2 py-1 text-sm border rounded dark:bg-gray-800 dark:border-gray-600"
                      placeholder="Descripción"
                      rows={2}
                    />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <input
                      type="number"
                      value={newTreatment.duracion_min}
                      onChange={(e) => handleInputChange('duracion_min', e.target.value ? Number(e.target.value) : '')}
                      className="w-full px-2 py-1 text-sm border rounded dark:bg-gray-800 dark:border-gray-600"
                      placeholder="Minutos"
                      min="0"
                    />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <input
                      type="number"
                      step="0.01"
                      value={newTreatment.precio_eur}
                      onChange={(e) => handleInputChange('precio_eur', e.target.value ? Number(e.target.value) : '')}
                      className="w-full px-2 py-1 text-sm border rounded dark:bg-gray-800 dark:border-gray-600"
                      placeholder="0.00"
                      min="0"
                    />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300">
                      Activo
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex space-x-2">
                      <button
                        onClick={handleSaveNew}
                        disabled={createMutation.isPending}
                        className="text-green-600 hover:text-green-900 dark:text-green-400 dark:hover:text-green-300 disabled:opacity-50"
                        title="Guardar"
                      >
                        ✓
                      </button>
                      <button
                        onClick={handleCancelNew}
                        disabled={createMutation.isPending}
                        className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300 disabled:opacity-50"
                        title="Cancelar"
                      >
                        ✕
                      </button>
                    </div>
                  </td>
                </tr>
              )}

              {/* Filas de tratamientos existentes */}
              {treatments?.map((treatment) => (
                <tr key={treatment.id} className={editingTreatmentId === treatment.id ? 'bg-yellow-50 dark:bg-yellow-900/20' : ''}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-gray-100">
                    {treatment.id}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-gray-100">
                    {editingTreatmentId === treatment.id ? (
                      <input
                        type="text"
                        value={editingTreatment.nombre}
                        onChange={(e) => handleEditInputChange('nombre', e.target.value)}
                        className="w-full px-2 py-1 text-sm border rounded dark:bg-gray-800 dark:border-gray-600"
                        placeholder="Nombre *"
                        required
                      />
                    ) : (
                      treatment.nombre
                    )}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400">
                    {editingTreatmentId === treatment.id ? (
                      <textarea
                        value={editingTreatment.descripcion}
                        onChange={(e) => handleEditInputChange('descripcion', e.target.value)}
                        className="w-full px-2 py-1 text-sm border rounded dark:bg-gray-800 dark:border-gray-600"
                        placeholder="Descripción"
                        rows={2}
                      />
                    ) : (
                      <span className="max-w-xs block">{treatment.descripcion || '-'}</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    {editingTreatmentId === treatment.id ? (
                      <input
                        type="number"
                        value={editingTreatment.duracion_min}
                        onChange={(e) => handleEditInputChange('duracion_min', e.target.value ? Number(e.target.value) : '')}
                        className="w-full px-2 py-1 text-sm border rounded dark:bg-gray-800 dark:border-gray-600"
                        placeholder="Minutos"
                        min="0"
                      />
                    ) : (
                      formatDuration(treatment.duracion_min || treatment.duracion_minutos)
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    {editingTreatmentId === treatment.id ? (
                      <input
                        type="number"
                        step="0.01"
                        value={editingTreatment.precio_eur}
                        onChange={(e) => handleEditInputChange('precio_eur', e.target.value ? Number(e.target.value) : '')}
                        className="w-full px-2 py-1 text-sm border rounded dark:bg-gray-800 dark:border-gray-600"
                        placeholder="0.00"
                        min="0"
                      />
                    ) : (
                      formatPrice(treatment.precio_eur || treatment.precio)
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {editingTreatmentId === treatment.id ? (
                      <select
                        value={editingTreatment.activo.toString()}
                        onChange={(e) => handleEditInputChange('activo', e.target.value === 'true')}
                        className="w-full px-2 py-1 text-sm border rounded dark:bg-gray-800 dark:border-gray-600"
                      >
                        <option value="true">Activo</option>
                        <option value="false">Inactivo</option>
                      </select>
                    ) : (
                      <StatusSwitch
                        isActive={isTreatmentActive(treatment.activo)}
                        onToggle={() => handleToggleStatus(treatment)}
                        loading={updateStatusMutation.isPending}
                      />
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex space-x-2">
                      {editingTreatmentId === treatment.id ? (
                        <>
                          <button
                            onClick={handleSaveEdit}
                            disabled={updateMutation.isPending}
                            className="text-green-600 hover:text-green-900 dark:text-green-400 dark:hover:text-green-300 disabled:opacity-50"
                            title="Guardar"
                          >
                            ✓
                          </button>
                          <button
                            onClick={handleCancelEdit}
                            disabled={updateMutation.isPending}
                            className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300 disabled:opacity-50"
                            title="Cancelar"
                          >
                            ✕
                          </button>
                        </>
                      ) : (
                        <button
                          onClick={() => handleEdit(treatment)}
                          disabled={isAdding || editingTreatmentId !== null}
                          className="text-primary-600 hover:text-primary-900 dark:text-primary-400 dark:hover:text-primary-300 disabled:opacity-50"
                          title="Editar"
                        >
                          <PencilIcon className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {(!treatments || treatments.length === 0) && !isAdding && (
          <div className="text-center py-12">
            <p className="text-gray-500 dark:text-gray-400">
              {t('common.noData')}
            </p>
          </div>
        )}
      </div>

      {/* Paginación */}
      <div className="flex items-center justify-between mt-4">
        <div className="text-sm text-gray-600 dark:text-gray-400">
          Mostrando {treatments.length} de {total} tratamientos
        </div>
        <div className="flex items-center gap-2">
          <button disabled={page === 1} onClick={() => setPage(p => Math.max(1, p - 1))} className="btn btn-xs">Anterior</button>
          <span className="text-sm">Página {page}</span>
          <button disabled={page * limit >= total} onClick={() => setPage(p => p + 1)} className="btn btn-xs">Siguiente</button>
          <select value={limit} onChange={e => { setLimit(Number(e.target.value)); setPage(1); }} className="input input-xs ml-2 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 border-gray-300 dark:border-gray-600">
            {[5, 10, 20, 50].map(opt => <option key={opt} value={opt}>{opt} por página</option>)}
          </select>
        </div>
      </div>
    </div>
  );
} 